/**
 * Dictation (ADR-0029): the store apps use the platform's speech recogniser
 * through @capacitor-community/speech-recognition (Android SpeechRecognizer,
 * iOS SFSpeechRecognizer; both work offline when the device has the language
 * downloaded). The PWA uses the browser's Web Speech API where it exists.
 * The transcript is returned exactly as recognised: free text stays as spoken
 * (ADR-0011).
 */
import { isNative } from '../device';

export type SpeechError = 'UNSUPPORTED' | 'DENIED' | 'FAILED';

/** BCP 47 tag for the interface language, preferring the device's own region (es-CO over es-ES). */
export function speechLocale(lang: string): string {
  if (lang.includes('-')) return lang;
  const device = typeof navigator !== 'undefined' ? navigator.language : '';
  if (device?.toLowerCase().startsWith(`${lang}-`)) return device;
  return ({ es: 'es-ES', pt: 'pt-BR', en: 'en-GB', fr: 'fr-FR', de: 'de-DE', it: 'it-IT', nl: 'nl-NL', pl: 'pl-PL' } as Record<string, string>)[lang] ?? lang;
}

interface WebRecognition {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((ev: { results: ArrayLike<ArrayLike<{ transcript: string }>> }) => void) | null;
  onerror: ((ev?: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
}

function webCtor(): (new () => WebRecognition) | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as { SpeechRecognition?: new () => WebRecognition; webkitSpeechRecognition?: new () => WebRecognition };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

export interface Listening {
  /** Resolves with the transcript ('' if nothing was heard), or rejects with a SpeechError. */
  result: Promise<string>;
  stop(): void;
}

/** Listen for one utterance. */
export function listen(lang: string): Listening {
  const locale = speechLocale(lang);
  if (isNative()) {
    let stopFn = () => {};
    const result = (async () => {
      const { SpeechRecognition } = await import('@capacitor-community/speech-recognition');
      stopFn = () => void SpeechRecognition.stop().catch(() => {});
      if (!(await SpeechRecognition.available()).available) throw 'UNSUPPORTED' as SpeechError;
      let perm = await SpeechRecognition.checkPermissions();
      if (perm.speechRecognition !== 'granted') perm = await SpeechRecognition.requestPermissions();
      if (perm.speechRecognition !== 'granted') throw 'DENIED' as SpeechError;
      try {
        const r = await SpeechRecognition.start({ language: locale, maxResults: 1, partialResults: false, popup: false });
        return (r?.matches?.[0] ?? '').trim();
      } catch {
        throw 'FAILED' as SpeechError;
      }
    })();
    return { result, stop: () => stopFn() };
  }

  const Ctor = webCtor();
  if (!Ctor) return { result: Promise.reject('UNSUPPORTED' as SpeechError), stop: () => {} };
  const rec = new Ctor();
  rec.lang = locale;
  rec.continuous = false;
  rec.interimResults = false;
  const result = new Promise<string>((resolve, reject) => {
    let text = '';
    rec.onresult = (ev) => {
      text = Array.from(ev.results)
        .map((r) => r[0]?.transcript ?? '')
        .join(' ')
        .trim();
    };
    rec.onerror = (ev) => reject((ev?.error === 'not-allowed' ? 'DENIED' : 'FAILED') as SpeechError);
    rec.onend = () => resolve(text);
  });
  try {
    rec.start();
  } catch {
    return { result: Promise.reject('FAILED' as SpeechError), stop: () => {} };
  }
  return { result, stop: () => rec.stop() };
}
